import type { SidekickRecipient } from '../../contexts/SidekickApiContext';
import type { ShareRecipient } from './types';

/**
 * Mirrors `validEmailRegex` in `@brightdrop/bd-form`, the repository's
 * source of truth for address shape. It is copied rather than imported
 * because that package's entry point reaches `AddressLookup`, whose
 * `@mapbox/search-js-react` peer this package has no other reason to
 * carry.
 */
const VALID_EMAIL_REGEX =
  /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

/**
 * Whether an address is well-formed enough to send to.
 *
 * @param email - The address to check.
 * @returns `true` when the address parses as an email.
 */
export const isValidEmail = (email: string): boolean =>
  VALID_EMAIL_REGEX.test(email);

/**
 * The form an address is compared in.
 *
 * Folding case is what keeps one person from being chipped twice,
 * once picked from the directory and once typed back in a different
 * case. Only the comparison folds: a chip still carries the address
 * as it was given.
 *
 * @param email - The address to fold.
 * @returns The address, trimmed and lowercased.
 */
export const normalizeEmail = (email: string): string =>
  email.trim().toLowerCase();

/**
 * A recipient's directory name, or an empty string when it has none.
 *
 * Whitespace counts as none. A name of spaces would otherwise reach
 * the chip and its remove label as a blank, leaving both invisible.
 *
 * @param recipient - The recipient to name.
 * @returns The trimmed display name, or an empty string.
 */
export const getRecipientName = ({ displayName }: SidekickRecipient): string =>
  displayName?.trim() ?? '';

/**
 * The text a recipient's chip and suggestion row carry.
 *
 * @param recipient - The recipient to label.
 * @returns The display name for a named directory match, the address otherwise.
 */
export const getRecipientLabel = (recipient: SidekickRecipient): string => {
  const name = getRecipientName(recipient);

  return name === '' ? recipient.email : name;
};

/**
 * Turns free-typed text into a recipient keyed on itself.
 *
 * @param email - Raw text the user entered, untrimmed.
 * @returns The recipient, tagged as hand-typed.
 */
export const toTypedRecipient = (email: string): ShareRecipient => {
  const trimmed = email.trim();

  return { id: trimmed, email: trimmed, source: 'typed' };
};
